import React, { useState } from 'react';
import { toast } from 'sonner';
import { api, mensajeError } from '../../../lib/api';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { AlertTriangle, Eye, History, Loader2, RotateCcw, X } from 'lucide-react';
import { BOTON_VERDE, FOCO, cuando, numero } from './comun';

/**
 * Las instrucciones del bot (su «prompt»). Viven en el servidor con todas sus
 * versiones: guardar crea SIEMPRE una versión nueva, con una nota corta para
 * saber qué cambió. Una versión vieja se lee sin poder tocarla y «Restaurar»
 * la guarda como versión nueva («restaura la vN»). El texto lo pega dirección:
 * el código no lleva ninguno.
 *
 * El texto del editor vive en BotWhatsApp para que «Probar el bot» pueda
 * usarlo sin guardar.
 */

export const LIMITE_INSTRUCCIONES = 200000;
const LIMITE_NOTA = 120;
const MARCAS = [
  ['{{CONTEXTO_LEAD}}', 'el bot no recibirá los datos del lead (nombre, tratamiento, plazo…).'],
  ['## Datos del lead', 'no se puede separar la parte fija de las instrucciones para guardarla en caché: cada respuesta costará más.'],
];

/** Lo que le falta al texto para que el servidor lo monte bien. */
export function avisosInstrucciones(texto) {
  if (!texto || !texto.trim()) return [];
  return MARCAS.filter(([marca]) => !texto.includes(marca));
}

export default function InstruccionesBot({ bot, texto, onTexto, onCambio }) {
  const vigente = bot.prompt || null;
  const versiones = Array.isArray(bot.versiones) ? bot.versiones : [];
  const [nota, setNota] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [vista, setVista] = useState(null); // versión vieja abierta en solo lectura
  const [cargandoVersion, setCargandoVersion] = useState(null);
  const [restaurando, setRestaurando] = useState(false);

  const original = vigente?.texto || '';
  const cambiado = texto !== original;
  const largo = texto.length;
  const demasiado = largo > LIMITE_INSTRUCCIONES;
  const avisos = avisosInstrucciones(texto);
  const puedeGuardar = cambiado && !!texto.trim() && !!nota.trim() && !demasiado && !guardando;

  let pista = '';
  if (!cambiado) pista = vigente ? 'Cambia algo en el texto para guardar una versión nueva.' : 'Pega las instrucciones para guardar la primera versión.';
  else if (!texto.trim()) pista = 'Las instrucciones no pueden quedar vacías.';
  else if (demasiado) pista = `Como mucho ${numero(LIMITE_INSTRUCCIONES)} caracteres.`;
  else if (!nota.trim()) pista = 'Escribe una nota corta: qué cambia en esta versión.';

  const guardar = async (e) => {
    e?.preventDefault();
    if (!puedeGuardar) return;
    setGuardando(true);
    try {
      const r = await api.post('/api/whatsapp/bot/prompt', { texto, nota: nota.trim() });
      setNota('');
      onCambio(r.data);
      const v = r.data?.prompt?.version;
      toast.success(v ? `Instrucciones guardadas como versión ${v}` : 'Instrucciones guardadas');
    } catch (err) {
      toast.error(mensajeError(err, 'No se han podido guardar las instrucciones'));
    } finally {
      setGuardando(false);
    }
  };

  const deshacer = () => {
    if (!window.confirm('¿Deshacer los cambios del editor? Vuelve el texto de la versión vigente.')) return;
    onTexto(original);
  };

  const ver = async (version) => {
    if (cargandoVersion) return;
    setCargandoVersion(version);
    try {
      const r = await api.get(`/api/whatsapp/bot/prompt/${version}`);
      setVista(r.data && typeof r.data === 'object' ? r.data : null);
    } catch (err) {
      toast.error(mensajeError(err, `No se ha podido leer la versión ${version}`));
    } finally {
      setCargandoVersion(null);
    }
  };

  const restaurar = async () => {
    if (!vista || restaurando) return;
    const aviso = cambiado ? ' Lo que has cambiado en el editor sin guardar se pierde.' : '';
    if (!window.confirm(`¿Restaurar la versión ${vista.version}? Se guarda como versión nueva y el bot la usa `
      + `desde ya.${aviso}`)) return;
    setRestaurando(true);
    try {
      const r = await api.post('/api/whatsapp/bot/prompt', {
        texto: vista.texto || '', nota: `restaura la v${vista.version}`,
      });
      setVista(null);
      onCambio(r.data);
      const v = r.data?.prompt?.version;
      toast.success(v ? `Versión ${vista.version} restaurada como versión ${v}` : 'Versión restaurada');
    } catch (err) {
      toast.error(mensajeError(err, 'No se ha podido restaurar la versión'));
    } finally {
      setRestaurando(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="text-sm" data-testid="bot-prompt-vigente">
        {vigente ? (
          <>
            <span className="font-semibold text-[#13231E]">Versión {vigente.version} vigente</span>
            <span className="text-muted-foreground">
              {[vigente.at ? ` · ${cuando(vigente.at)}` : '', vigente.por ? ` · ${vigente.por}` : ''].join('')}
            </span>
            {vigente.nota ? <span className="text-muted-foreground"> · «{vigente.nota}»</span> : null}
          </>
        ) : (
          <span className="text-amber-800">Todavía no hay instrucciones: pégalas aquí y guárdalas como versión 1.</span>
        )}
      </div>

      <form onSubmit={guardar} className="space-y-2">
        <label htmlFor="bot-prompt-texto" className="sr-only">Instrucciones del bot</label>
        <textarea id="bot-prompt-texto" value={texto} onChange={(e) => onTexto(e.target.value)} spellCheck={false}
          className={`block w-full h-[60vh] min-h-[18rem] resize-y rounded-md border border-input bg-background px-3 py-2 font-mono text-[12.5px] leading-5 ${FOCO}`}
          data-testid="bot-prompt-texto" aria-describedby="bot-prompt-contador" />
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
          <span id="bot-prompt-contador" className={`tabular-nums ${demasiado ? 'text-red-700 font-semibold' : 'text-muted-foreground'}`}
            data-testid="bot-prompt-contador">
            {numero(largo)} caracteres de {numero(LIMITE_INSTRUCCIONES)}
            {cambiado ? ' · con cambios sin guardar' : ''}
          </span>
        </div>
        {avisos.length ? (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 space-y-0.5"
            data-testid="bot-prompt-avisos" role="status">
            {avisos.map(([marca, efecto]) => (
              <div key={marca} className="flex items-start gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" aria-hidden="true" />
                <span>Falta <code className="font-mono">{marca}</code>: {efecto}</span>
              </div>
            ))}
          </div>
        ) : null}
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[14rem] space-y-1">
            <label htmlFor="bot-prompt-nota" className="text-xs font-medium text-[#13231E]">
              Nota de la versión (obligatoria)
            </label>
            <Input id="bot-prompt-nota" value={nota} maxLength={LIMITE_NOTA} onChange={(e) => setNota(e.target.value)}
              placeholder="Qué cambia, en pocas palabras" aria-required="true" data-testid="bot-prompt-nota" />
          </div>
          <Button type="submit" disabled={!puedeGuardar} className={BOTON_VERDE} data-testid="bot-prompt-guardar">
            {guardando ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : null}
            Guardar como versión nueva
          </Button>
          {cambiado && vigente ? (
            <Button type="button" variant="ghost" onClick={deshacer} data-testid="bot-prompt-deshacer">
              Deshacer los cambios
            </Button>
          ) : null}
        </div>
        {pista ? <p className="text-xs text-muted-foreground" data-testid="bot-prompt-pista">{pista}</p> : null}
      </form>

      <details className="border-t pt-3" data-testid="bot-versiones">
        <summary className={`cursor-pointer select-none text-sm font-medium text-[#13231E] inline-flex items-center gap-1.5 rounded ${FOCO}`}>
          <History className="w-4 h-4" aria-hidden="true" /> Historial de versiones ({versiones.length})
        </summary>
        {versiones.length ? (
          <ul className="mt-2 divide-y rounded-md border max-h-72 overflow-y-auto">
            {versiones.map((v) => {
              const esVigente = vigente && v.version === vigente.version;
              return (
                <li key={v.version} className="flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-2 text-sm"
                  data-testid="bot-version">
                  <span className="font-semibold tabular-nums">v{v.version}</span>
                  {esVigente ? (
                    <span className="rounded-full bg-emerald-100 px-1.5 text-[10.5px] font-semibold text-emerald-800">vigente</span>
                  ) : null}
                  <span className="text-muted-foreground text-xs">
                    {[cuando(v.at), v.por, v.caracteres !== undefined ? `${numero(v.caracteres)} caracteres` : '']
                      .filter(Boolean).join(' · ')}
                  </span>
                  {v.nota ? <span className="text-xs basis-full sm:basis-auto">«{v.nota}»</span> : null}
                  <Button type="button" variant="outline" size="sm" className="ml-auto" onClick={() => ver(v.version)}
                    disabled={cargandoVersion === v.version} data-testid={`bot-version-ver-${v.version}`}>
                    {cargandoVersion === v.version
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
                      : <Eye className="w-3.5 h-3.5" aria-hidden="true" />}
                    Ver
                  </Button>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="mt-2 text-sm text-muted-foreground">Todavía no hay ninguna versión guardada.</p>
        )}
      </details>

      {vista ? (
        <div className="rounded-lg border bg-muted/30 p-3 space-y-2" data-testid="bot-version-vista"
          role="region" aria-label={`Versión ${vista.version} en solo lectura`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm">
              <span className="font-semibold">Versión {vista.version}</span>
              <span className="text-muted-foreground"> · solo lectura</span>
              <span className="text-muted-foreground text-xs block">
                {[cuando(vista.at), vista.por, vista.nota ? `«${vista.nota}»` : ''].filter(Boolean).join(' · ')}
              </span>
            </div>
            <div className="flex gap-2">
              {vigente && vista.version === vigente.version ? (
                <span className="text-xs text-muted-foreground self-center">Es la vigente</span>
              ) : (
                <Button type="button" size="sm" onClick={restaurar} disabled={restaurando} className={BOTON_VERDE}
                  data-testid="bot-version-restaurar">
                  {restaurando ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> : <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" />}
                  Restaurar
                </Button>
              )}
              <Button type="button" size="icon" variant="ghost" onClick={() => setVista(null)}
                aria-label="Cerrar la versión" data-testid="bot-version-cerrar">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <textarea readOnly value={vista.texto || ''} aria-label={`Texto de la versión ${vista.version}`}
            className={`block w-full h-72 resize-y rounded-md border border-input bg-background px-3 py-2 font-mono text-[12px] leading-5 text-muted-foreground ${FOCO}`}
            data-testid="bot-version-texto" />
        </div>
      ) : null}
    </div>
  );
}
